import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';
import { Observable, zip } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { TravelerPaxResponse, LoyaltyResponse } from '@pax/entities/traveler-pax';
import { PaxFormService } from '@pax/services/pax-form.service';
import { TravelerInfoType, TravelerTypes } from '@pax/entities/traveler-info';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { FormErrors } from '@reservation/entities/form-errors';
import { BookingPaxTypeDocumentProps } from '@app/entities/booking-documents';
import { PaxDocumentsPipe } from '@pax/pipes/pax-documents.pipe';
import {
  getPaxDocumentAbreviationById, internationalPaxDocumentFilledOutWithAbreviation
} from '@forms/mappers/form-document-input.mapper';
import { BookingFormGeneralErrorTypes } from '@app/entities/booking-form-general-error';
import { BookingDocumentPaxIdType } from '@app/entities/booking-pax-documents-id';
import { PaxFacade } from '@pax/pax.facade';
import { getBookingInfoTypeByTravelerInfoType } from '@pax/mappers/get-pax-info-by-types.mapper';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import {
  TypographyFontFamily, TypographyFontSize, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';
import { DataActionCardInfoType } from '@design-system/components/templates/data-action-card/entities/data-action-card.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { PaxDataAction } from '@pax/entities/pax-data-actions';

@Component({
  selector: 'app-pax-data-resumed',
  templateUrl: './pax-data-resumed.component.html',
  styleUrls: ['./pax-data-resumed.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PaxDataResumedComponent {

  @Input() type: TravelerTypes;
  @Input() paxData: TravelerPaxResponse;
  @Input() programs: FrequentFlyerResponse[] = [];
  @Input() showFrequentFlyer: boolean;
  @Input() defaultAirlineProgram: string;
  @Input() isAnInternationalTrip: boolean;
  @Input() paxError: boolean;
  @Input() travelerInfoType: TravelerInfoType;
  @Input() paxSectionErrors: FormErrors[] = [];
  @Input() bookingSource: HotelProvider;
  @Input() disabledDocumentInputs: boolean;
  @Input() travelerBookingProvider: TravelBookingType;

  @Output() openList: EventEmitter<void> = new EventEmitter();
  @Output() openListPrograms: EventEmitter<void> = new EventEmitter();
  @Output() openMarriottProgram: EventEmitter<void> = new EventEmitter();
  @Output() openFrequenPassengerForm: EventEmitter<LoyaltyResponse> = new EventEmitter();
  @Output() openPassportForm: EventEmitter<void> = new EventEmitter();

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  public readonly dataActionCardInfoType = DataActionCardInfoType;

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly paxDataAction = PaxDataAction;

  constructor(
    private paxFormService: PaxFormService,
    private paxDocumentsPipe: PaxDocumentsPipe,
    private paxFacade: PaxFacade,
  ) { }

  get documents$(): Observable<BookingPaxTypeDocumentProps[]> {
    return this.paxDocumentsPipe.transform(this.type, this.paxData?.nationality);
  }

  get documentAbreviation$(): Observable<string> {
    return this.documents$.pipe(map(documents =>
      getPaxDocumentAbreviationById(documents, this.paxData?.configuration_document_id)));
  }

  get internationalDocumentFilledOut$(): Observable<boolean> {
    return this.documentAbreviation$.pipe(map(abreviation =>
      internationalPaxDocumentFilledOutWithAbreviation(abreviation, this.isAnInternationalTrip)));
  }

  get activatePassportSection$(): Observable<boolean> {
    return this.internationalDocumentFilledOut$.pipe(map(internationalDocumentFilledOut =>
      !this.disabledDocumentInputs && this.isAnInternationalTrip && !internationalDocumentFilledOut));
  }

  private get havePassport$(): Observable<boolean> {
    return zip(
      this.documents$,
      this.paxFacade.travelers$.pipe(map(travelers => {
        const traveler = travelers.find(_traveler => _traveler._id === this.paxData?._id);
        return traveler?.documents || [];
      }))
    ).pipe(
      map(([validDocuments, paxDocuments]) => {
        return paxDocuments.some(_document => {
          const documentId = Number(_document.type);
          const document = validDocuments.find(doc => doc.DOCUMENT_ID === documentId);
          return !!document && document.TYPE === BookingDocumentPaxIdType.PAS;
        });
      }));
  }

  get canShowFrequentNumber(): boolean {
    return this.showFrequentFlyer && !!this.type;
  }

  get hasFrequentFlyerProgram(): boolean {
    return !!this.paxData && !isEmpty(this.selectedLoyalty);
  }

  get showLoyaltyList(): boolean {
    return !!this.paxData && !isEmpty(this.paxData.loyalty);
  }

  get typeIcon(): string {
    return this.type === TravelerTypes.ADULTS ? Icon.PersonOutline : Icon.Children;
  }

  get selectedLoyalty(): LoyaltyResponse {
    return this.paxFormService.getLoyaltyPax(this.paxData);
  }

  get addNoLoyaltyClass(): boolean {
    return this.type === TravelerTypes.ADULTS && !this.hasFrequentFlyerProgram;
  }

  get travelerLabel(): string {
    return getBookingInfoTypeByTravelerInfoType(this.travelerInfoType);
  }

  get hasPassportRestrictionError(): boolean {
    return this.hasErrorByType(BookingFormGeneralErrorTypes.PASSPORT_RESTRICTION);
  }

  get isMarriottBookingsource(): boolean {
    return this.bookingSource === HotelProvider.MarriottBonvoy;
  }

  get hasBonvoyNumber(): boolean {
    return this.paxData?.bonvoy_number?.length > 0;
  }

  get isComplete(): boolean {
    return this.travelerBookingProvider === TravelBookingType.HOTELS ?
      this.hasBonvoyNumber : this.hasFrequentFlyerProgram;
  }

  public hasErrorByType(type: BookingFormGeneralErrorTypes): boolean {
    return !!this.paxSectionErrors && !!this.paxSectionErrors.find(error => !!Object.keys(error) &&
    Object.keys(error).find(key => key as BookingFormGeneralErrorTypes === type));
  }

  public errorValue(): string {
    switch(true) {
      case this.hasPassportRestrictionError:
        return 'BOOKING_MODULE.PAX.ERRORS.MANDATORY_INTERNATIONAL_DOCUMENT';
      case this.hasErrorByType(BookingFormGeneralErrorTypes.DOCUMENT_ERROR):
        return 'BOOKING_MODULE.PAX.ERRORS.PAX_DOCUMENT_ERROR';
      case this.hasErrorByType(BookingFormGeneralErrorTypes.PATTERN):
        return 'BOOKING_MODULE.PAX.ERRORS.PAX_PATTERN_ERROR';
      case this.hasErrorByType(BookingFormGeneralErrorTypes.INVALID_BONVOY_NUMBER):
        return 'BOOKING_MODULE.PAX.ERRORS.MARRIOTT_BONVOY_NUMBER_ERROR';
      default:
        return 'BOOKING_MODULE.PAX.ERRORS.PAX_DATA_ERROR'
    }
  }

  public openBySelectedLoyalty(): void {
    if (this.selectedLoyalty && this.selectedLoyalty._id) this.openListPrograms.emit();
    else this.openFrequenPassengerForm.emit(this.selectedLoyalty);
  }

  public openMarriotBonvoyForm(): void {
    this.openMarriottProgram.emit();
  }

  public openByHasFrequentFlyerProgram(): void {
    if (this.showLoyaltyList) this.openListPrograms.emit();
    else this.openFrequenPassengerForm.emit();
  }

  public togglePassportForm(): void {
    this.havePassport$.pipe(
      take(1),
      tap(havePassport => {
        if (havePassport) this.openList.emit();
        else this.openPassportForm.emit();
      })
    ).subscribe().unsubscribe();
  }

  public dataActionEvent(identifier: PaxDataAction): void {
    switch (identifier) {
      case PaxDataAction.PaxInfo:
        this.openList.emit();
        break;
      case PaxDataAction.AddLoyalty:
        this.openByHasFrequentFlyerProgram();
        break;
      case PaxDataAction.AddPassport:
        this.togglePassportForm();
        break;
      case PaxDataAction.LoyaltyInfo:
        this.openBySelectedLoyalty();
        break;
      case PaxDataAction.Marriott:
        this.openMarriotBonvoyForm();
        break;
    }
  }
}
